import { memo } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import {
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { AUTO_SYNC_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { AutoSyncConfig, AutoSyncOptions } from '../types';

export interface AutoSyncSectionProps {
  value: AutoSyncConfig;
  onChange: (value: AutoSyncConfig) => void;
}

const AutoSyncSectionInner = ({ value, onChange }: AutoSyncSectionProps) => {
  const { classes, cx } = useStyles();

  const handleAutoSyncEnabledChange = (checked: boolean) => {
    onChange({
      enabled: checked,
      options: {
        prune: false,
        selfHeal: false,
        allowEmpty: false,
      },
    });
  };

  const handleAutoSyncOptionChange = (
    field: keyof AutoSyncOptions,
    checked: boolean,
  ) => {
    const updatedOptions: AutoSyncOptions = {
      ...value.options,
      [field]: checked,
    };
    onChange({
      enabled: value.enabled,
      options: updatedOptions,
    });
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Auto Sync Configuration"
          description="Customize the ArgoCD application's automated sync policy."
          variant="h5"
          divider
          infoDoc={AUTO_SYNC_INFO}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Enable Auto Sync"
            description="Enable or disable automatic synchronization for the ArgoCD application."
          />
          <FormControlLabel
            control={
              <Checkbox
								checked={value.enabled}
                color="secondary"
                onChange={e => handleAutoSyncEnabledChange(e.target.checked)}
              />
            }
            label="Enable Auto Sync"
          />
        </Box>
        <Box
          className={cx(classes.inputContainer, classes.flexColumnContainer)}
        >
          <ScaffolderInputHeader
            title="Auto Sync Options"
            description="Configure the options for automatic synchronization of the ArgoCD application."
          />
          <FormControlLabel
            disabled={!value.enabled}
            control={
              <Checkbox
								checked={value.options?.prune ?? false}
                color="secondary"
                onChange={e =>
                  handleAutoSyncOptionChange('prune', e.target.checked)
                }
              />
            }
            label="Prune - Delete resources that are no longer defined in Git"
          />
          <FormControlLabel
            disabled={!value.enabled}
            control={
              <Checkbox
								checked={value.options?.selfHeal ?? false}
                color="secondary"
                onChange={e =>
                  handleAutoSyncOptionChange('selfHeal', e.target.checked)
                }
              />
            }
            label="Self Heal - Automatically correct drift from desired state"
          />
          <FormControlLabel
            disabled={!value.enabled}
            control={
              <Checkbox
								checked={value.options?.allowEmpty ?? false}
                color="secondary"
                onChange={e =>
                  handleAutoSyncOptionChange('allowEmpty', e.target.checked)
                }
              />
            }
            label="Allow Empty - Permit sync when no resources are found"
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const AutoSyncSection = memo(AutoSyncSectionInner);
